import React, { useState, useRef, useEffect } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { useTopPosts, TopPost } from '@/hooks/useTopPosts';
import { MessageCircle, Heart, User } from 'lucide-react';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { LikeButton } from '@/components/social/LikeButton';
import { CommentButton } from '@/components/social/CommentButton';
import { EmptyStateCard } from '@/components/ui/EmptyStateCard';

const SocialMatSection: React.FC = () => {
  const { t } = useI18n();
  const { data: posts, isLoading, error, refetch } = useTopPosts();
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const queryClient = useQueryClient();

  // Forçar recarregamento quando o usuário retorna para a página Home
  useEffect(() => {
    if (location.pathname === '/home') {
      // Invalidar queries relacionadas para forçar recarregamento
      queryClient.invalidateQueries({ queryKey: ['top-posts'] });
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
      queryClient.invalidateQueries({ queryKey: ['connection-suggestions'] });
      
      // Forçar refetch imediato
      refetch();
    }
  }, [location.pathname, queryClient, refetch]);

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Calculate total number of pages based on visible cards
  // Show dots even for small numbers of posts
  const cardsPerView = 2; // Reduced to 2 cards per view to ensure we have pages
  const totalPages = posts ? Math.max(1, Math.ceil(posts.length / cardsPerView)) : 1;

  // Handle scroll to update current index
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const cardWidth = 256; // w-64 = 256px
      const newIndex = Math.round(scrollLeft / cardWidth);
      setCurrentIndex(Math.min(newIndex, totalPages - 1));
    }
  };

  // Scroll to specific page
  const scrollToPage = (pageIndex: number) => {
    if (scrollContainerRef.current) {
      const cardWidth = 256; // w-64 = 256px
      const scrollLeft = pageIndex * cardWidth;
      scrollContainerRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Auto-scroll effect - DESABILITADO para manter o carrossel estático
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     if (posts && posts.length > cardsPerView) {
  //       const nextIndex = (currentIndex + 1) % totalPages;
  //       scrollToPage(nextIndex);
  //     }
  //   }, 5000); // Auto-scroll every 5 seconds

  //   return () => clearInterval(interval);
  // }, [currentIndex, posts, totalPages]);

  if (isLoading) {
    return (
      <div className="mb-8">
        <h2 className="text-xl font-bold text-white mb-4">{t('home.social_mat.title')}</h2>
        <div className="relative max-w-[360px] mx-auto">
          <div className="w-full overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex space-x-4 pl-5">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex-shrink-0 w-64 bg-[rgb(45,36,36)] rounded-lg p-4 animate-pulse">
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 bg-gray-700 rounded-full mr-3"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-700 rounded mb-1"></div>
                      <div className="h-3 bg-gray-700 rounded w-1/4"></div>
                    </div>
                  </div>
                  <div className="h-4 bg-gray-700 rounded mb-2"></div>
                  <div className="h-3 bg-gray-700 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <h2 className="text-xl font-bold text-white mb-4">{t('home.social_mat.title')}</h2>
        <div className="text-red-400 text-center py-8">
          Error loading posts. Please try again.
        </div>
      </div>
    );
  }

  const hasPosts = posts && posts.length > 0;

  return (
    <div className="mb-8">
      <h2 className="text-sm font-bold text-white mb-4">{t('home.social_mat.title')}</h2>
      
      {!hasPosts ? (
        <EmptyStateCard
          title={t('home.social_mat.no_posts')}
          description={t('home.social_mat.no_posts_description')}
          buttonText={t('home.social_mat.create_post')}
          onButtonClick={() => window.location.href = '/social'}
        />
      ) : (
        <>
          <div className="relative overflow-hidden">
            <div 
              className="w-full overflow-x-auto pb-2 scrollbar-hide" 
              ref={scrollContainerRef}
              onScroll={handleScroll}
            >
              <div className="flex space-x-4">
                {posts.map((post) => (
                  <div key={post.id} className="flex-shrink-0 w-64 bg-[rgb(45,36,36)] rounded-2xl shadow-md py-4 px-4">
                    {/* Author Info */}
                    <div className="flex items-center space-x-3 mb-3">
                      <Link to={post.user.username ? `/profile/${post.user.username}` : '#'} className="flex items-center space-x-3 group" tabIndex={post.user.username ? 0 : -1} aria-disabled={!post.user.username}>
                        <div className="flex-shrink-0">
                          <FramedAvatar
                            src={post.user.profile_picture_url}
                            alt={post.user.full_name}
                            initials={post.user.full_name ? post.user.full_name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase() : 'A'}
                            frameSize={{ w: 32, h: 36 }}
                            beltRank={post.user.belt_rank || 'white'}
                            fontSize={11}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 min-w-0">
                            <span className="font-semibold text-white text-sm truncate max-w-[60%] group-hover:underline">{post.user.full_name || 'Anonymous'}</span>
                            <span className="text-xs text-gray-400 truncate max-w-[40%]">• {formatTimeAgo(post.created_at)}</span>
                          </div>
                          <div className="text-xs text-gray-400 font-light">{post.user.academy_name || 'No academy'}</div>
                        </div>
                      </Link>
                    </div>
                    
                    {/* Content */}
                    <div className="mb-3">
                      <p className="text-gray-100 break-words post-content text-sm leading-relaxed overflow-hidden" style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {post.content}
                      </p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center space-x-8 pt-2">
                      <LikeButton 
                        postId={post.id} 
                        className="text-gray-200 hover:text-sossiety-red-600"
                      />
                      <CommentButton
                        commentCount={0}
                        onClick={() => {}}
                        className="text-gray-200 hover:text-sossiety-red-600"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation dots - Always show at least one dot */}
          <div className="flex justify-center space-x-1.5 mt-4">
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index}
                onClick={() => scrollToPage(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-gray-300 scale-125' 
                    : 'bg-gray-600 hover:bg-gray-500'
                }`}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default SocialMatSection;
